import { defineMessages, type MessageDescriptor } from 'react-intl';

const intlMessages = {
	cloudIdLabel: {
		id: 'twg-microsite.debugPanel.cloudIdLabel.non-final',
		defaultMessage: 'Cloud ID',
		description: 'Label for the cloud ID input in the Debug Panel custom data form',
	},
	cloudIdPlaceholder: {
		id: 'twg-microsite.debugPanel.cloudIdPlaceholder.non-final',
		defaultMessage: 'e.g. a436116f-02ce-4520-8fbb-7301462a1674',
		description: 'Placeholder text for the cloud ID input in the Debug Panel custom data form',
	},
	orgIdLabel: {
		id: 'twg-microsite.debugPanel.orgIdLabel.non-final',
		defaultMessage: 'Org ID',
		description: 'Label for the org ID input in the Debug Panel custom data form',
	},
	orgIdPlaceholder: {
		id: 'twg-microsite.debugPanel.orgIdPlaceholder.non-final',
		defaultMessage: 'e.g. 2346a038-3c8c-498b-a79b-e7847859868d',
		description: 'Placeholder text for the org ID input in the Debug Panel custom data form',
	},
	viewDataButton: {
		id: 'twg-microsite.debugPanel.viewDataButton.non-final',
		defaultMessage: 'View data',
		description: 'Submit button label for the custom data form in the Debug Panel',
	},
	staticDataHelperText: {
		id: 'twg-microsite.debugPanel.staticDataHelperText.non-final',
		defaultMessage:
			'Pick a pre-defined dataset to seed the logged-in metrics view without making a network request.',
		description: 'Helper text shown above static datasets in the Debug Panel',
	},
	title: {
		id: 'twg-microsite.debugPanel.title.non-final',
		defaultMessage: 'Data Simulation',
		description: 'Main heading for the Debug Panel',
	},
	description: {
		id: 'twg-microsite.debugPanel.description.non-final',
		defaultMessage:
			'This section allows you to simulate data for different scenarios as different organisations have verying levels of graph density and breadth.',
		description: 'Description text explaining what the Debug Panel does',
	},
	instructionsIntro: {
		id: 'twg-microsite.debugPanel.instructionsIntro.non-final',
		defaultMessage: 'You can run this simulation one of the two ways:',
		description: 'Introductory sentence before listing simulation options',
	},
	instructionsStatic: {
		id: 'twg-microsite.debugPanel.instructionsStatic.non-final',
		defaultMessage: 'Choose from one of the pre-defined sets of static datasets below',
		description: 'First list item describing static dataset option in the Debug Panel',
	},
	instructionsCustom: {
		id: 'twg-microsite.debugPanel.instructionsCustom.non-final',
		defaultMessage: 'Provide your own {cloudId} and {orgId} details',
		description:
			'Second list item describing custom cloudId/orgId option in the Debug Panel instructions',
	},
	staticTabLabel: {
		id: 'twg-microsite.debugPanel.staticTabLabel.non-final',
		defaultMessage: 'Static Data',
		description: 'Tab label for static dataset mode in the Debug Panel',
	},
	customTabLabel: {
		id: 'twg-microsite.debugPanel.customTabLabel.non-final',
		defaultMessage: 'Custom Data',
		description: 'Tab label for custom data mode in the Debug Panel',
	},
	resetButton: {
		id: 'twg-microsite.debugPanel.resetButton.non-final',
		defaultMessage: 'Reset',
		description: 'Button label that reloads the page and resets the Debug Panel state',
	},
	closePanelLabel: {
		id: 'twg-microsite.debugPanel.closePanelLabel.non-final',
		defaultMessage: 'Close debug panel',
		description: 'Accessibility label for the debug panel close control',
	},
	featureGatesSectionTitle: {
		id: 'twg-microsite.debugPanel.featureGatesSectionTitle.non-final',
		defaultMessage: 'Feature Gates',
		description: 'Section header for the feature-gate override controls in the Debug Panel',
	},
	explorationV2SectionTitle: {
		id: 'twg-microsite.debugPanel.explorationV2SectionTitle.non-final',
		defaultMessage: 'UI V2',
		description: 'Section title for the exploration v2 feature gate override controls',
	},
	explorationV2SectionHelper: {
		id: 'twg-microsite.debugPanel.explorationV2SectionHelper.non-final',
		defaultMessage:
			'Overrides are written to localStorage (STATSIG_OVERRIDES), matching the Statsig feature-gate client. The page reloads when you change this so the home view can pick up the new value.',
		description: 'Helper text explaining how exploration v2 overrides are stored and applied',
	},
	explorationV2ToggleLabel: {
		id: 'twg-microsite.debugPanel.explorationV2ToggleLabel.non-final',
		defaultMessage: 'Force exploration v2 on',
		description:
			'Accessible label for the toggle that forces the twg_microsite_exploration_v2 feature gate on or off',
	},
	datasetAccentureLabel: {
		id: 'twg-microsite.debugPanel.dataset.accenture.label.non-final',
		defaultMessage: 'Accenture',
		description: 'Label for the Accenture static dataset option in the Debug Panel',
	},
	datasetAccentureDescription: {
		id: 'twg-microsite.debugPanel.dataset.accenture.description.non-final',
		defaultMessage: 'Large enterprise tenant with broad Jira footprint.',
		description: 'Description for the Accenture static dataset option in the Debug Panel',
	},
	datasetAllegionLabel: {
		id: 'twg-microsite.debugPanel.dataset.allegion.label.non-final',
		defaultMessage: 'Allegion',
		description: 'Label for the Allegion static dataset option in the Debug Panel',
	},
	datasetAllegionDescription: {
		id: 'twg-microsite.debugPanel.dataset.allegion.description.non-final',
		defaultMessage: 'Mid-size tenant, low goal coverage.',
		description: 'Description for the Allegion static dataset option in the Debug Panel',
	},
	datasetAtlassianLabel: {
		id: 'twg-microsite.debugPanel.dataset.atlassian.label.non-final',
		defaultMessage: 'Atlassian',
		description: 'Label for the Atlassian static dataset option in the Debug Panel',
	},
	datasetAtlassianDescription: {
		id: 'twg-microsite.debugPanel.dataset.atlassian.description.non-final',
		defaultMessage: 'Densest internal graph; superuser of every product.',
		description: 'Description for the Atlassian static dataset option in the Debug Panel',
	},
	datasetCommunardoLabel: {
		id: 'twg-microsite.debugPanel.dataset.communardo.label.non-final',
		defaultMessage: 'Communardo',
		description: 'Label for the Communardo static dataset option in the Debug Panel',
	},
	datasetCommunardoDescription: {
		id: 'twg-microsite.debugPanel.dataset.communardo.description.non-final',
		defaultMessage: 'Smaller customer with focused activity.',
		description: 'Description for the Communardo static dataset option in the Debug Panel',
	},
	datasetRedditLabel: {
		id: 'twg-microsite.debugPanel.dataset.reddit.label.non-final',
		defaultMessage: 'Reddit',
		description: 'Label for the Reddit static dataset option in the Debug Panel',
	},
	datasetRedditDescription: {
		id: 'twg-microsite.debugPanel.dataset.reddit.description.non-final',
		defaultMessage: 'Heavy Jira usage with strong code linkage.',
		description: 'Description for the Reddit static dataset option in the Debug Panel',
	},
	datasetProcoreLargestLabel: {
		id: 'twg-microsite.debugPanel.dataset.procoreLargest.label.non-final',
		defaultMessage: 'Procore (largest)',
		description: 'Label for the largest Procore static dataset option in the Debug Panel',
	},
	datasetProcoreLargestDescription: {
		id: 'twg-microsite.debugPanel.dataset.procoreLargest.description.non-final',
		defaultMessage: 'Largest Procore tenant with rich connector activity.',
		description: 'Description for the largest Procore static dataset option in the Debug Panel',
	},
	datasetHelloFreshLabel: {
		id: 'twg-microsite.debugPanel.dataset.helloFresh.label.non-final',
		defaultMessage: 'HelloFresh',
		description: 'Label for the HelloFresh static dataset option in the Debug Panel',
	},
	datasetHelloFreshDescription: {
		id: 'twg-microsite.debugPanel.dataset.helloFresh.description.non-final',
		defaultMessage: 'Operations-heavy tenant.',
		description: 'Description for the HelloFresh static dataset option in the Debug Panel',
	},
	datasetWorkdayLabel: {
		id: 'twg-microsite.debugPanel.dataset.workday.label.non-final',
		defaultMessage: 'Workday',
		description: 'Label for the Workday static dataset option in the Debug Panel',
	},
	datasetWorkdayDescription: {
		id: 'twg-microsite.debugPanel.dataset.workday.description.non-final',
		defaultMessage: 'Mid-size tenant with very high artifact density per issue.',
		description: 'Description for the Workday static dataset option in the Debug Panel',
	},
	datasetWorkdayLargestLabel: {
		id: 'twg-microsite.debugPanel.dataset.workdayLargest.label.non-final',
		defaultMessage: 'Workday (largest)',
		description: 'Label for the largest Workday static dataset option in the Debug Panel',
	},
	datasetWorkdayLargestDescription: {
		id: 'twg-microsite.debugPanel.dataset.workdayLargest.description.non-final',
		defaultMessage: 'Largest Workday tenant with the highest artifacts-per-issue ratio.',
		description: 'Description for the largest Workday static dataset option in the Debug Panel',
	},
} as const;

export const messages: typeof intlMessages = defineMessages(intlMessages) satisfies Record<
	string,
	MessageDescriptor
>;
